import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';

import { authGet } from '../../../../utils/request';
import { IconStat } from '../../../../assets/icon';

const REPORT_ZONE = 'America/Denver';
const triggerClassName = 'fixed right-0 top-1/2 z-[90] -translate-y-1/2 rounded-l-lg '
  + 'bg-[#009ef7] px-3 py-4 text-sm font-semibold text-white shadow-lg hover:bg-[#0089d7]';
const panelClassName = 'absolute bottom-0 right-0 top-0 flex w-full max-w-[460px] '
  + 'flex-col bg-white shadow-2xl dark:bg-[#1b263b]';
const activePeriodClassName = 'bg-[#009ef7] text-white';
const inactivePeriodClassName = 'bg-gray-100 text-[#5e6278] dark:bg-[#26354d] dark:text-white';
const categories = [
  ['order_part_box_by', 'Part box'],
  ['order_assembled_by', 'Assembled'],
  ['order_packed_by', 'Packed'],
  ['Reassembly', 'Reassembly'],
  ['Sending', 'Sending'],
  ['Service', 'Service'],
];

const getRange = (period) => {
  const today = DateTime.now().setZone(REPORT_ZONE).startOf('day');
  const start = period === 'week' ? today.startOf('week') : today.minus({ days: 6 });
  return { from: start.toISODate(), to: today.toISODate() };
};

const PersonalReportDrawer = () => {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState('week');
  const [refresh, setRefresh] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const range = useMemo(() => getRange(period), [period, refresh, open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    setReport(null);
    authGet('/general/file/personal-report', { params: range })
      .then(({ data }) => {
        if (active) setReport(data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, range.from, range.to, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const total = report?.total ?? 0;
  const previousTotal = report?.previous?.total ?? 0;
  const change = total - previousTotal;
  const previousRange = report?.previous;
  const previousLabel = `Previous period: ${previousTotal} · ${previousRange?.from} – ${previousRange?.to}`;

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => setOpen(true)}
        aria-label="Open my order statistics"
      >
        <IconStat />
      </button>
      {open && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-default bg-black/40"
            aria-label="Close statistics"
            onClick={() => setOpen(false)}
          />
          <aside
            className={panelClassName}
            role="dialog"
            aria-modal="true"
            aria-labelledby="personal-report-title"
          >
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5 dark:border-[#415a77]">
              <div>
                <h2 id="personal-report-title" className="m-0 text-xl font-semibold text-[#273047] dark:text-white">
                  My order statistics
                </h2>
                <p className="mb-0 mt-1 text-xs text-gray-500">Personal results · America/Denver time</p>
              </div>
              <button
                type="button"
                className="text-2xl leading-none text-gray-500"
                onClick={() => setOpen(false)}
                aria-label="Close statistics"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5">
              <div className="mb-2 flex gap-2" role="group" aria-label="Statistics period">
                {[
                  ['week', 'This week'],
                  ['rolling', 'Last 7 days'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPeriod(value)}
                    aria-pressed={period === value}
                    className={`rounded-md px-3 py-2 text-sm font-medium ${
                      period === value ? activePeriodClassName : inactivePeriodClassName
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mb-5 text-xs text-gray-500">{`${range.from} – ${range.to}`}</p>
              {loading && <p role="status" className="text-sm text-gray-500">Loading statistics…</p>}
              {error && (
                <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                  Could not load statistics.
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={() => setRefresh((value) => value + 1)}
                  >
                    Retry
                  </button>
                </div>
              )}
              {!loading && !error && report && (
                <>
                  <div className="rounded-xl bg-[#f5f8fa] p-5 dark:bg-[#26354d]">
                    <div className="text-sm text-gray-500 dark:text-gray-300">Actions recorded</div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <strong className="text-4xl text-[#273047] dark:text-white">{total}</strong>
                      <span className={`text-sm font-semibold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {`${change >= 0 ? '+' : ''}${change} vs previous period`}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">{previousLabel}</div>
                  </div>
                  <div className="mt-6">
                    <h3 className="mb-1 text-base font-semibold text-[#273047] dark:text-white">Work by category</h3>
                    <div className="mb-4 flex gap-4 text-xs text-gray-500">
                      <span>
                        <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-[#009ef7]" />
                        Selected period
                      </span>
                      <span>
                        <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-[#a1a5b7]" />
                        Previous period
                      </span>
                    </div>
                    <div className="space-y-5" aria-label="Actions by category compared with the previous period">
                      {categories.map(([key, label]) => {
                        const current = report.counts?.[key] ?? 0;
                        const previous = previousRange?.counts?.[key] ?? 0;
                        const maximum = Math.max(current, previous, 1);
                        return (
                          <div key={key}>
                            <div className="mb-1 flex justify-between text-sm text-[#273047] dark:text-white">
                              <span>{label}</span>
                              <span>{`${current} / ${previous}`}</span>
                            </div>
                            <div className="space-y-1">
                              <div className="h-2 rounded-full bg-gray-100 dark:bg-[#26354d]">
                                <div
                                  className="h-2 rounded-full bg-[#009ef7]"
                                  style={{ width: `${(current / maximum) * 100}%` }}
                                />
                              </div>
                              <div className="h-2 rounded-full bg-gray-100 dark:bg-[#26354d]">
                                <div
                                  className="h-2 rounded-full bg-[#a1a5b7]"
                                  style={{ width: `${(previous / maximum) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {total === 0 && previousTotal === 0 && (
                    <p className="mt-5 text-sm text-gray-500">No recorded actions in either period.</p>
                  )}
                  <p className="mt-6 text-xs text-gray-500">Categories may count the same order at different stages.</p>
                  <button
                    type="button"
                    className="mt-4 text-sm font-medium text-[#009ef7]"
                    onClick={() => setRefresh((value) => value + 1)}
                  >
                    Refresh
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default PersonalReportDrawer;
